import React from 'react'
import { Helmet } from 'react-helmet-async'

const AboutPage: React.FC = () => (
  <>
    <Helmet>
      <title>About AI Decision Maker - Free Online Decision Assistant with AI Analysis | Decision Making Platform</title>
      <meta name="description" content="Learn about AI Decision Maker - the comprehensive decision-making platform with 4 AI analysis tools and 14 specialized decision wheels. Free, privacy-focused, and available 24/7. The best AI decision assistant." />
      <meta name="keywords" content="about ai decision maker, decision tool, ai analysis, smart decisions, decision assistant, free online tool, decision making software, ai decision analysis, decision platform, ai decision assistant" />
      <meta property="og:title" content="About AI Decision Maker - Free Online Decision Assistant with AI Analysis" />
      <meta property="og:description" content="Learn about AI Decision Maker - the comprehensive decision-making platform with 4 AI analysis tools and 14 specialized decision wheels. Free AI decision assistant." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://aidecision.org/about" />
      <meta property="og:image" content="https://aidecision.org/about-og-image.png" />
      <meta name="twitter:title" content="About AI Decision Maker - Free Online Decision Assistant" />
      <meta name="twitter:description" content="Learn about AI Decision Maker - comprehensive decision-making platform with AI analysis." />
      <meta name="twitter:image" content="https://aidecision.org/about-twitter-image.png" />
      <link rel="canonical" href="https://aidecision.org/about" />
      
      {/* Enhanced Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "AI Decision Maker",
          "alternateName": "AI Decision Assistant",
          "description": "Comprehensive decision-making platform with 4 AI analysis tools and 14 specialized decision wheels. Free online AI decision maker.",
          "url": "https://aidecision.org",
          "logo": "https://aidecision.org/logo.png",
          "foundingDate": "2024",
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "support@aidecision.org",
            "contactType": "customer service",
            "url": "https://aidecision.org/contact"
          },
          "sameAs": [
            "https://aidecision.org"
          ],
          "knowsAbout": [
            "AI Decision Making",
            "Decision Analysis",
            "Spinning Wheel Tools",
            "Random Selection",
            "Decision Support Systems"
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "AI Decision Maker Tools",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "AI Decision Analysis",
                  "description": "AI-powered decision analysis tools"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Decision Wheels",
                  "description": "Specialized decision wheels for various scenarios"
                }
              }
            ]
          }
        })}
      </script>
    </Helmet>

    <div className="max-w-4xl mx-auto py-12 px-4">
      <header className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-6 text-gray-900">About AI Decision Maker</h1>
        <p className="text-xl text-gray-600">The comprehensive decision-making platform with 4 AI analysis tools and 14 specialized decision wheels</p>
      </header>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">Our Mission</h2>
        <p className="mb-6 text-gray-700 text-lg leading-relaxed">
          <strong>AI Decision Maker</strong> is a comprehensive decision-making platform designed to revolutionize how individuals and teams approach choices. By offering 4 advanced AI analysis tools and 14 specialized decision wheels, we provide a complete solution for every decision-making scenario.
        </p>
        <p className="mb-6 text-gray-700 leading-relaxed">
          Our mission is to democratize intelligent decision-making by making AI-powered analysis accessible to everyone. Whether you're making personal choices, team decisions, or complex business strategies, our platform provides the tools and insights you need to make confident, informed decisions.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">What Makes AI Decision Maker Unique</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <article>
            <h3 className="text-xl font-medium mb-3 text-gray-900">🤖 4 Advanced AI Analysis Tools</h3>
            <p className="text-gray-700">Our platform offers comprehensive AI analysis including Pros & Cons Analysis, Causal Chain Analysis, Context Analysis, and Multi-option Analysis for deep decision insights.</p>
          </article>
          <article>
            <h3 className="text-xl font-medium mb-3 text-gray-900">🎯 14 Specialized Decision Wheels</h3>
            <p className="text-gray-700">From name pickers to sports team selectors, we provide specialized wheels for every scenario, making random selection both fun and practical.</p>
          </article>
          <article>
            <h3 className="text-xl font-medium mb-3 text-gray-900">📋 Professional Decision Templates</h3>
            <p className="text-gray-700">Pre-built templates for career changes, home purchases, educational choices, and relationship decisions with relevant options and criteria.</p>
          </article>
          <article>
            <h3 className="text-xl font-medium mb-3 text-gray-900">🌐 24/7 AI Availability</h3>
            <p className="text-gray-700">Get instant AI insights anytime, anywhere. Our AI analysis is available 24/7, completely free, with no usage limits for registered users.</p>
          </article>
          <article>
            <h3 className="text-xl font-medium mb-3 text-gray-900">🔒 Privacy-Focused Design</h3>
            <p className="text-gray-700">Your data stays local and private. We don't collect or store personal information, ensuring complete privacy and security for all users.</p>
          </article>
          <article>
            <h3 className="text-xl font-medium mb-3 text-gray-900">💡 User-Friendly Interface</h3>
            <p className="text-gray-700">Intuitive design that makes complex AI analysis accessible to users of all technical levels, with responsive design for all devices.</p>
          </article>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">Our AI Analysis Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <article className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-3 text-gray-900">⚖️ Pros & Cons Analysis</h3>
            <p className="text-gray-700">Detailed evaluation of options with pros, cons, risk assessment, confidence scoring, and actionable next steps for informed decision-making.</p>
          </article>
          <article className="bg-green-50 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-3 text-gray-900">🔗 Causal Chain Analysis</h3>
            <p className="text-gray-700">Visualize decision outcomes through causal chains, understanding the ripple effects and long-term impacts of your choices.</p>
          </article>
          <article className="bg-purple-50 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-3 text-gray-900">👤 Context Analysis</h3>
            <p className="text-gray-700">Personalized analysis using user profiles and context, providing tailored advice based on your specific situation and preferences.</p>
          </article>
          <article className="bg-orange-50 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-3 text-gray-900">📊 Multi-option Analysis</h3>
            <p className="text-gray-700">Systematic comparison of multiple options with weighted criteria, scoring, and ranking for complex decision scenarios.</p>
          </article>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">Specialized Decision Wheels</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-2">👤</div>
            <h4 className="font-medium text-gray-900">Name & Team Pickers</h4>
            <p className="text-sm text-gray-600">Random name selection and team grouping</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-2">✅</div>
            <h4 className="font-medium text-gray-900">Yes/No & Number Pickers</h4>
            <p className="text-sm text-gray-600">Binary choices and random number generation</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-2">🌍</div>
            <h4 className="font-medium text-gray-900">Geography Pickers</h4>
            <p className="text-sm text-gray-600">Countries, states, and location-based choices</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-2">🎨</div>
            <h4 className="font-medium text-gray-900">Creative Pickers</h4>
            <p className="text-sm text-gray-600">Colors, letters, and design inspiration</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-2">🏈</div>
            <h4 className="font-medium text-gray-900">Sports Team Pickers</h4>
            <p className="text-sm text-gray-600">MLB, NBA, NFL team selection</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-2">🎡</div>
            <h4 className="font-medium text-gray-900">Custom Picker</h4>
            <p className="text-sm text-gray-600">Flexible wheel for any custom options</p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">How AI Decision Maker Works</h2>
        <div className="bg-blue-50 rounded-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <article className="text-center">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="text-lg font-medium mb-2">1. Choose Your Tool</h3>
              <p className="text-gray-700">Select from our 4 AI analysis tools or 14 specialized decision wheels based on your specific decision-making needs.</p>
            </article>
            <article className="text-center">
              <div className="text-3xl mb-3">🤖</div>
              <h3 className="text-lg font-medium mb-2">2. Get AI Insights</h3>
              <p className="text-gray-700">Receive instant AI-powered analysis with detailed evaluations, risk assessments, and personalized recommendations.</p>
            </article>
            <article className="text-center">
              <div className="text-3xl mb-3">💡</div>
              <h3 className="text-lg font-medium mb-2">3. Make Confident Decisions</h3>
              <p className="text-gray-700">Use comprehensive insights and actionable next steps to make informed, confident decisions with full understanding of implications.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">Use Cases for AI Decision Maker</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <article>
            <h3 className="text-lg font-medium mb-2 text-gray-900">Personal Life Decisions</h3>
            <p className="text-gray-700">Career changes, home purchases, relationship choices, and daily decisions with AI-powered analysis and professional templates.</p>
          </article>
          <article>
            <h3 className="text-lg font-medium mb-2 text-gray-900">Team & Group Decisions</h3>
            <p className="text-gray-700">Fair team selections, project prioritization, and collaborative decision-making using our specialized wheels and AI analysis.</p>
          </article>
          <article>
            <h3 className="text-lg font-medium mb-2 text-gray-900">Educational Applications</h3>
            <p className="text-gray-700">Student selection, course planning, learning path decisions, and classroom activities with educational templates.</p>
          </article>
          <article>
            <h3 className="text-lg font-medium mb-2 text-gray-900">Business & Strategic Decisions</h3>
            <p className="text-gray-700">Investment choices, strategy selection, resource allocation, and complex business decisions with systematic analysis.</p>
          </article>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">Key Features & Benefits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-3 text-gray-900">🎯 Comprehensive Analysis</h3>
            <ul className="text-gray-700 space-y-2">
              <li>• 4 different AI analysis approaches</li>
              <li>• Risk assessment and mitigation strategies</li>
              <li>• Confidence scoring and recommendations</li>
              <li>• Actionable next steps and timelines</li>
            </ul>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-3 text-gray-900">⚡ Instant Results</h3>
            <ul className="text-gray-700 space-y-2">
              <li>• 24/7 AI availability</li>
              <li>• Real-time analysis and insights</li>
              <li>• No waiting or processing delays</li>
              <li>• Immediate decision support</li>
            </ul>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-3 text-gray-900">🔒 Privacy & Security</h3>
            <ul className="text-gray-700 space-y-2">
              <li>• Local data storage</li>
              <li>• No personal information collection</li>
              <li>• Complete privacy protection</li>
              <li>• Secure data handling</li>
            </ul>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium mb-3 text-gray-900">💎 Free & Accessible</h3>
            <ul className="text-gray-700 space-y-2">
              <li>• 100% free to use</li>
              <li>• No hidden costs or limitations</li>
              <li>• Works on all devices</li>
              <li>• No registration required</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">Our Team</h2>
        <p className="mb-6 text-gray-700 leading-relaxed">
          We are a passionate team of developers, designers, AI specialists, and decision-making experts dedicated to building the most comprehensive and intelligent decision-making platform available. Our diverse backgrounds in technology, psychology, and business inform our approach to creating tools that truly enhance human decision-making capabilities.
        </p>
        <p className="text-gray-700 leading-relaxed">
          Our commitment to excellence drives us to continuously innovate and improve AI Decision Maker, ensuring it remains the most reliable, comprehensive, and user-friendly decision-making platform in the world.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">Contact Information</h2>
        <div className="bg-gray-50 rounded-lg p-6">
          <p className="mb-4 text-gray-700">
            <strong>Email:</strong> <a href="mailto:support@aidecision.org" className="text-blue-600 underline hover:text-blue-800">support@aidecision.org</a>
          </p>
          <p className="mb-4 text-gray-700">
            <strong>Purpose:</strong> For feedback, support, partnership inquiries, feature requests, or general questions about AI Decision Maker.
          </p>
          <p className="text-gray-700">
            We value your input and are committed to making AI Decision Maker the most comprehensive and effective decision-making platform available. Your feedback helps us continuously improve our tools and services.
          </p>
        </div>
      </section>

      <footer className="text-center text-gray-500 text-sm mt-12 pt-8 border-t border-gray-200">
        <p>AI Decision Maker &copy; {new Date().getFullYear()} | All rights reserved.</p>
        <p className="mt-2">Empowering smarter decisions through comprehensive AI analysis and specialized decision tools.</p>
      </footer>
    </div>
  </>
)

export default AboutPage 